import React, { useState, useRef, useEffect } from "react";
import {
  MoreVertical,
  Clock,
  Star,
  Pencil,
  Copy,
  Archive,
  Trash2,
  X,
} from "lucide-react";

interface ProjectCardProps {
  id: string;
  title: string;
  lastUpdated: string;
  thumbnail: string;
  favorite: boolean;
  status: string;
  link: string;
  ownedDrill: boolean;
  createdByName: string;
  setSelectedDrill: () => void;
  duplicateDrill: () => void;
  setShowDeleteModal: (show: boolean) => void;
  setDrillAsTemplate: () => void;
  userData: any;
  isTemplate: boolean;
  handleUseTemplate: () => void;
}

export default function ProjectCard({
  id,
  title,
  lastUpdated,
  thumbnail,
  favorite,
  status,
  link,
  ownedDrill,
  createdByName,
  setSelectedDrill,
  duplicateDrill,
  setShowDeleteModal,
  setDrillAsTemplate,
  userData,
  isTemplate,
  handleUseTemplate,
}: ProjectCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [isFavorite, setIsFavorite] = useState(favorite);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target as Node)
      ) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "complete":
        return "bg-green-100 text-green-800";
      case "in progress":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };
  if (isTemplate) {
    return (
      <div ref={cardRef} className="relative">
        <button
          className="group block bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all"
          onClick={() => setShowTemplateModal(true)}
        >
          <div className="aspect-video relative overflow-hidden rounded-t-xl">
            <img
              src={thumbnail}
              alt={title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors">
              
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900">{title}</h3>
              <span
                className={`text-xs px-2 py-1 rounded-full ${getStatusColor(
                  status
                )}`}
              >
                {status}
              </span>
            </div>
            <div className="flex items-center text-sm text-gray-500">
      {createdByName}
            </div>
          </div>
        </button>

        {showTemplateModal && (
          <div className="fixed inset-0 z-50">
            <div 
              className="fixed inset-0 bg-black/50"
              onClick={() => setShowTemplateModal(false)}
            />
            <div className="fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white p-6 shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Template Overview</h2>
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="aspect-video relative overflow-hidden rounded-lg mb-4">
                <img
                  src={thumbnail}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900">{title}</h3>
                  <p className="text-sm text-gray-500">Last updated {lastUpdated}</p>
                  <p className="text-sm text-gray-500">Created by {createdByName}</p>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowTemplateModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleUseTemplate();
                      setShowTemplateModal(false);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Use Template
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div ref={cardRef} className="relative">
      <a
        className="group block bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all"
        href={`/designs/${link}`}
      >
        <div className="aspect-video relative overflow-hidden rounded-t-xl">
          <img
            src={thumbnail}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors">
            {ownedDrill == true && (
              <div className="absolute top-2 right-2">
                <button
                  ref={menuButtonRef}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setShowMenu(!showMenu);
                  }}
                  className="p-1.5 bg-white/90 rounded-full hover:bg-white transition-colors"
                >
                  <MoreVertical className="h-4 w-4 text-gray-600" />
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900">{title}</h3>
            <span
              className={`text-xs px-2 py-1 rounded-full ${getStatusColor(
                status
              )}`}
            >
              {status}
            </span>
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            Last updated {lastUpdated}
          </div>
        </div>
      </a>

      {/* Dropdown Menu */}
      {showMenu && (
        <div
          ref={menuRef}
          className="absolute z-50 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1"
          style={{
            top: "2.5rem",
            right: "0.5rem",
          }}
        >
          {/* <button className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2">
            <Pencil className="h-4 w-4" /> Edit
          </button> */}
          {duplicateDrill && (
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
            onClick={() => {
              setSelectedDrill();

              duplicateDrill(id);
            }}
          >
            <Copy className="h-4 w-4" /> Duplicate
          </button>
          )}
          <button
            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
            onClick={() => {
              setSelectedDrill();

              setShowDeleteModal(true);
            }}
          >
            <Trash2 className="h-4 w-4" /> Delete
          </button>
          {userData.isAdmin && (
            <button
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
              onClick={() => {
                setSelectedDrill();

                setDrillAsTemplate(id);
              }}
            >
              <Star className="h-4 w-4" /> Set as Template
            </button>
          )}
        </div>
      )}
    </div>
  );
}
